import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Heart, ShoppingCart, User, LogOut } from 'lucide-react';
import { ROUTES } from '@/config/routes';
import { useAppSelector, useAppDispatch } from '@/store';
import { uiActions } from '@/store/slices/uiSlice';
import { authActions } from '@/store/slices/authSlice';
import { wishlistActions } from '@/store/slices/wishlistSlice';
import { MAIN_NAV } from '@/constants/navigation';
import { HeaderNav } from './HeaderNav';
import { MobileMenu } from './MobileMenu';
import { SearchOverlay } from './SearchOverlay';
import { CategorySidebar } from './CategorySidebar';
import { authService } from '@/services/api/auth.service';
import { customerWishlistService } from '@/services/api/customer-wishlist.service';
import { generateId } from '@/utils/helpers';

export function Header() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const cartCount = useAppSelector((state) => state.cart.items.length);
  const wishlistCount = useAppSelector((state) => state.wishlist.items.length);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isMobileMenuOpen = useAppSelector((state) => state.ui.isMobileMenuOpen);
  const isSearchOpen = useAppSelector((state) => state.ui.isSearchOpen);
  const [isCategorySidebarOpen, setIsCategorySidebarOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(wishlistActions.clearWishlist());
      return;
    }
    let isMounted = true;
    customerWishlistService.list()
      .then((items) => {
        if (isMounted) dispatch(wishlistActions.setItems(items));
      })
      .catch(() => undefined);
    return () => { isMounted = false; };
  }, [dispatch, isAuthenticated]);

  const handleLogout = async () => {
    await authService.logout();
    dispatch(authActions.logout());
    dispatch(uiActions.addToast({ id: generateId(), message: 'Logged out successfully.', type: 'success' }));
    navigate(ROUTES.HOME);
  };

  return (
    <>
      {/* Main Header */}
      <header className="sticky top-0 z-40 bg-transparent text-zinc-900">
        <div className="relative mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          
          {/* Left section: Category Menu + Logo */}
          <div className="flex items-center gap-3">
            {/* Categories Sidebar Toggle (Custom Stylized 3-Lines) */}
            <button
              onClick={() => setIsCategorySidebarOpen(true)}
              className="group flex h-9 w-9 flex-col items-center justify-center gap-1 rounded-full text-zinc-900 transition-all duration-300 hover:bg-black/5"
              aria-label="Open categories menu"
            >
              <span className="h-[2px] w-5 bg-current transition-all duration-300" />
              <span className="h-[2px] w-5 bg-current transition-all duration-300" />
              <span className="h-[2px] w-5 bg-current transition-all duration-300" />
            </button>

            {/* Logo */}
            <Link to={ROUTES.HOME} className="flex shrink-0 items-center gap-1 sm:gap-2.5">
              <img src="/logo.png" alt="Beauty Sports Logo" className="h-7 w-auto object-contain sm:h-9 brightness-0" />
              <img src="/logo-text.png" alt="Beauty Sports" className="h-[15px] xs:h-[18px] min-[370px]:h-[21px] w-auto object-contain sm:h-8 brightness-0" />
            </Link>
          </div>

          {/* Center: Desktop Nav */}
          <HeaderNav items={MAIN_NAV} />

          {/* Right section: Icons + Pill Account Button */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Glassmorphic Pill for Icons */}
            <div className="flex h-10 items-center border border-black/10 bg-black/[0.04] backdrop-blur-md rounded-full px-2">
              <button
                onClick={() => dispatch(uiActions.setSearchOpen(true))}
                className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-900/80 hover:bg-black/5 hover:text-zinc-900 transition-colors"
                aria-label="Search"
              >
                <Search className="h-4 w-4" />
              </button>

              <Link
                to={ROUTES.WISHLIST}
                className="relative flex h-8 w-8 items-center justify-center rounded-full text-zinc-900/80 hover:bg-black/5 hover:text-zinc-900 transition-colors"
                aria-label={`Wishlist (${wishlistCount} items)`}
              >
                <Heart className="h-4 w-4" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-white scale-90">
                    {wishlistCount}
                  </span>
                )}
              </Link>

              <Link
                to={ROUTES.CART}
                className="relative flex h-8 w-8 items-center justify-center rounded-full text-zinc-900/80 hover:bg-black/5 hover:text-zinc-900 transition-colors"
                aria-label={`Cart (${cartCount} items)`}
              >
                <ShoppingCart className="h-4 w-4" />
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-accent text-[9px] font-bold text-white scale-90">
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>

            {/* Mobile Menu Toggle (User Icon on Mobile) */}
            <button
              onClick={() => dispatch(uiActions.toggleMobileMenu())}
              className="flex h-9 w-9 items-center justify-center rounded-full text-zinc-900/80 hover:bg-black/5 hover:text-zinc-900 lg:hidden transition-colors"
              aria-label="Open user menu"
            >
              <User className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
            </button>

            {/* Glassmorphic Pill for Auth/Account */}
            {isAuthenticated ? (
              <div className="hidden h-10 items-center gap-1 lg:flex border border-black/10 bg-black/[0.04] hover:bg-black/[0.08] backdrop-blur-md rounded-full px-2 transition-all">
                <Link
                  to={ROUTES.PROFILE}
                  className="flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold uppercase tracking-wider text-zinc-900"
                  aria-label="Account"
                >
                  <User className="h-4 w-4" />
                  Profile
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-900/70 hover:text-error transition-colors"
                  aria-label="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="hidden h-10 items-center lg:flex border border-black/10 bg-black/[0.04] hover:bg-black/[0.08] backdrop-blur-md rounded-full px-2 transition-all">
                <Link
                  to={ROUTES.LOGIN}
                  className="flex h-8 items-center rounded-full px-4 text-xs font-bold uppercase tracking-wider text-zinc-900"
                >
                  Login / Register
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <MobileMenu items={MAIN_NAV} />}

      {/* Search Overlay */}
      {isSearchOpen && <SearchOverlay />}

      {/* Categories Sidebar Drawer */}
      <CategorySidebar isOpen={isCategorySidebarOpen} onClose={() => setIsCategorySidebarOpen(false)} />
    </>
  );
}
